import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Languages, Loader2, Plus, RotateCcw, Save, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { http, errText } from "@/lib/http";
import { normalizeContent, useContentReload } from "@/content/ContentContext";
import { DEFAULT_CONTENT } from "@/content/defaults";
import { SCHEMAS, PROFILE_SCHEMA, TYPE_LABELS, CUSTOM_TYPES, MODELS, SLOTS, blankData } from "@/content/schemas";
import { REGISTRY } from "@/components/room/registry";
import { ObjectEditor, inputCls, Label } from "./Fields";

const clone = (o) => JSON.parse(JSON.stringify(o));

const AddSection = ({ sections, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [model, setModel] = useState("notebook");
  const used = sections.filter((s) => !REGISTRY[s.id]).map((s) => s.slot);
  const free = SLOTS.findIndex((_, i) => !used.includes(i));
  const add = () => {
    if (!label.trim()) return toast.error("Donnez un titre à la rubrique");
    if (free < 0) return toast.error(`Plus d'emplacement libre dans la chambre (${SLOTS.length} rubriques ajoutées maximum)`);
    onAdd({ id: `custom-${Date.now()}`, type, label: label.trim(), object: MODELS[model].label, model, slot: free, visible: true, data: blankData(type) });
    setLabel("");
    setOpen(false);
    return null;
  };
  if (!open) return <button className="chip w-full justify-center" onClick={() => setOpen(true)} data-testid="editor-add-section"><Plus className="h-4 w-4" />Ajouter une rubrique</button>;
  return (
    <div className="tile space-y-3" data-testid="editor-add-form">
      <div><Label>Titre</Label><input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex. Festivals" data-testid="editor-add-label" /></div>
      <div><Label>Type de contenu</Label>
        <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)} data-testid="editor-add-type">
          {CUSTOM_TYPES.map((t) => <option key={t} value={t} className="bg-[#1E1A17]">{TYPE_LABELS[t]}</option>)}
        </select>
      </div>
      <div><Label>Objet dans la chambre</Label>
        <div className="grid grid-cols-4 gap-1.5">
          {Object.entries(MODELS).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <button key={k} type="button" onClick={() => setModel(k)} title={m.label} data-testid={`editor-add-model-${k}`}
                className={`flex flex-col items-center gap-1 rounded-xl border p-2 text-[10px] ${model === k ? "border-[#E69C24] bg-[#E69C24]/15 text-[#F7F4EE]" : "border-[#F7F4EE]/10 text-[#C5BDB3]"}`}>
                <Icon className="h-4 w-4" />{m.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex gap-2">
        <button className="btn-gold flex-1 justify-center !py-2.5" onClick={add} data-testid="editor-add-confirm">Créer</button>
        <button className="chip" onClick={() => setOpen(false)}>Annuler</button>
      </div>
    </div>
  );
};

const SectionList = ({ draft, selected, setSelected, update }) => {
  const s = draft.sections;
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= s.length) return; const b = [...s]; [b[i], b[j]] = [b[j], b[i]]; update({ ...draft, sections: b }); };
  const remove = (i) => {
    if (!window.confirm(`Supprimer la rubrique « ${s[i].label} » et tout son contenu ?`)) return;
    update({ ...draft, sections: s.filter((_, k) => k !== i) });
    setSelected("profile");
  };
  const toggle = (i) => update({ ...draft, sections: s.map((x, k) => (k === i ? { ...x, visible: x.visible === false } : x)) });
  return (
    <div className="space-y-1.5">
      <button onClick={() => setSelected("profile")} data-testid="editor-select-profile"
        className={`flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm ${selected === "profile" ? "bg-[#E69C24] text-[#12100E]" : "text-[#F7F4EE] hover:bg-[#F7F4EE]/6"}`}>
        <UserRound className="h-4 w-4" />Profil & coordonnées
      </button>
      {s.map((x, i) => (
        <div key={x.id} className={`flex items-center gap-1 rounded-xl pr-1 ${selected === x.id ? "bg-[#F7F4EE]/10" : ""}`}>
          <button onClick={() => setSelected(x.id)} data-testid={`editor-select-${x.id}`}
            className={`min-w-0 flex-1 truncate px-3 py-2.5 text-left text-sm ${x.visible === false ? "text-[#8C8377] line-through" : "text-[#F7F4EE]"}`}>{x.label}</button>
          <button onClick={() => move(i, -1)} className="p-1 text-[#C5BDB3] hover:text-[#F7F4EE]" aria-label="Monter"><ArrowUp className="h-3.5 w-3.5" /></button>
          <button onClick={() => move(i, 1)} className="p-1 text-[#C5BDB3] hover:text-[#F7F4EE]" aria-label="Descendre"><ArrowDown className="h-3.5 w-3.5" /></button>
          <button onClick={() => toggle(i)} className="p-1 text-[#C5BDB3] hover:text-[#F7F4EE]" aria-label="Afficher / masquer" data-testid={`editor-toggle-${x.id}`}>
            {x.visible === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
          <button onClick={() => remove(i)} className="p-1 text-[#C5BDB3] hover:text-[#F2A38A]" aria-label="Supprimer" data-testid={`editor-delete-${x.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  );
};

const SectionForm = ({ section, onChange }) => {
  const custom = !REGISTRY[section.id];
  return (
    <div className="space-y-5" data-testid={`editor-form-${section.id}`}>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E6B877]">Type : {TYPE_LABELS[section.type] || section.type}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label>Titre de la rubrique</Label><input className={inputCls} value={section.label || ""} onChange={(e) => onChange({ ...section, label: e.target.value })} data-testid="editor-section-label" /></div>
        <div><Label>Nom de l'objet (sous-titre)</Label><input className={inputCls} value={section.object || ""} onChange={(e) => onChange({ ...section, object: e.target.value })} /></div>
      </div>
      {custom && (
        <div><Label>Objet dans la chambre</Label>
          <select className={inputCls} value={section.model} onChange={(e) => onChange({ ...section, model: e.target.value })}>
            {Object.entries(MODELS).map(([k, m]) => <option key={k} value={k} className="bg-[#1E1A17]">{m.label}</option>)}
          </select>
        </div>
      )}
      <ObjectEditor fields={SCHEMAS[section.type]} value={section.data} onChange={(data) => onChange({ ...section, data })} testid="editor" />
    </div>
  );
};

const LangBar = ({ lang, setLang, onTranslate, translating, hasEn }) => (
  <div className="tile flex flex-wrap items-center justify-between gap-3 !p-3" data-testid="editor-lang-bar">
    <div className="flex gap-1 rounded-full bg-[#F7F4EE]/5 p-1">
      {[["fr", "Version française"], ["en", "English version"]].map(([k, l]) => (
        <button key={k} onClick={() => setLang(k)} data-testid={`editor-lang-${k}`}
          className={`rounded-full px-4 py-1.5 text-sm transition-colors ${lang === k ? "bg-[#E69C24] text-[#12100E]" : "text-[#F7F4EE] hover:bg-[#F7F4EE]/8"}`}>{l}</button>
      ))}
    </div>
    {lang === "en" && (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-xs text-[#C5BDB3]" data-testid="editor-lang-status">
          {hasEn ? "Traduction enregistrée — corrigez-la librement ci-dessous." : "Pas encore de version anglaise : les visiteurs voient le français."}
        </p>
        <button onClick={onTranslate} disabled={translating} className="chip" data-testid="editor-translate">
          {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
          {translating ? "Traduction en cours…" : hasEn ? "Retraduire depuis le français" : "Traduire automatiquement"}
        </button>
      </div>
    )}
  </div>
);

export default function Editor() {
  const reload = useContentReload();
  const [lang, setLang] = useState("fr");
  const [hasEn, setHasEn] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [selected, setSelected] = useState("profile");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadFr = async () => clone(normalizeContent((await http.get("/content", { params: { lang: "fr" } })).data));

  useEffect(() => {
    setDraft(null);
    setDirty(false);
    http.get("/content", { params: { lang } }).then(async (r) => {
      const exists = Boolean(r.data?.profile);
      if (lang === "en") setHasEn(exists);
      setDraft(exists || lang === "fr" ? clone(normalizeContent(r.data)) : await loadFr());
    }).catch(() => setDraft(clone(DEFAULT_CONTENT)));
  }, [lang]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (!draft) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-[#E69C24]" data-testid="editor-loading" />;

  const switchLang = (l) => {
    if (l === lang) return;
    if (dirty && !window.confirm("Des modifications ne sont pas enregistrées. Changer de langue quand même ?")) return;
    setLang(l);
  };

  const translate = async () => {
    if (hasEn && !window.confirm("Retraduire tout le site depuis la version française enregistrée ? Vos corrections en anglais seront remplacées.")) return;
    setTranslating(true);
    try {
      const fr = await loadFr();
      const { data } = await http.post("/admin/translate", { profile: fr.profile, sections: fr.sections }, { timeout: 240000 });
      setDraft(clone(normalizeContent(data)));
      setHasEn(true);
      setDirty(false);
      await reload();
      toast.success("Version anglaise créée — relisez-la et corrigez si besoin");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setTranslating(false);
    }
  };

  const update = (d) => { setDraft(d); setDirty(true); };
  const section = draft.sections.find((s) => s.id === selected);

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/admin/content", { profile: draft.profile, sections: draft.sections }, { params: { lang } });
      setDirty(false);
      if (lang === "en") setHasEn(true);
      await reload();
      toast.success(lang === "en" ? "Version anglaise enregistrée" : "Modifications enregistrées — pensez à mettre à jour la version anglaise");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const msg = lang === "en"
      ? "Supprimer la version anglaise ? Les visiteurs verront le français jusqu'à la prochaine traduction."
      : "Revenir au contenu d'exemple ? Toutes vos modifications enregistrées seront effacées.";
    if (!window.confirm(msg)) return;
    await http.delete("/admin/content", { params: { lang } });
    setDraft(lang === "en" ? await loadFr() : clone(DEFAULT_CONTENT));
    if (lang === "en") setHasEn(false);
    setDirty(false);
    setSelected("profile");
    await reload();
    toast.success(lang === "en" ? "Version anglaise supprimée" : "Contenu d'exemple restauré");
  };

  return (
    <div className="space-y-5">
    <LangBar lang={lang} setLang={switchLang} onTranslate={translate} translating={translating} hasEn={hasEn} />
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]" data-testid="content-editor">
      <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
        <SectionList draft={draft} selected={selected} setSelected={setSelected} update={update} />
        <AddSection sections={draft.sections} onAdd={(s) => { update({ ...draft, sections: [...draft.sections, s] }); setSelected(s.id); }} />
        <button onClick={reset} className="flex items-center gap-2 px-3 text-xs text-[#B5AB9E] hover:text-[#F2A38A]" data-testid="editor-reset"><RotateCcw className="h-3.5 w-3.5" />{lang === "en" ? "Supprimer la version anglaise" : "Revenir au contenu d'exemple"}</button>
      </aside>
      <section className="tile min-w-0 space-y-5 p-5 sm:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F7F4EE]/10 pb-4">
          <h2 className="font-display text-xl text-[#F7F4EE]">{section ? section.label : "Profil & coordonnées"}</h2>
          <button onClick={save} disabled={saving || !dirty} className="btn-gold !py-2.5" data-testid="editor-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{dirty ? "Enregistrer" : "Enregistré"}
          </button>
        </div>
        {section ? (
          <SectionForm key={section.id} section={section} onChange={(s) => update({ ...draft, sections: draft.sections.map((x) => (x.id === s.id ? s : x)) })} />
        ) : (
          <div className="space-y-5" data-testid="editor-form-profile">
            <ObjectEditor fields={PROFILE_SCHEMA} value={draft.profile} onChange={(profile) => update({ ...draft, profile })} testid="editor-profile" />
          </div>
        )}
      </section>
    </div>
    </div>
  );
}
